import {
  HackleEventName,
  OverlayModal,
  customEvent,
  colors,
  getCookie,
  cooKeys,
  setCookie,
} from "goi_common";
import { SelectedOption } from ".";
import { QuotationRequestBody } from "@/types/quotationType";
import RequestUniqueFixedButton from "../RequestUniqueFixedButton";
import RequestCompareFixedButton from "../RequestCompareFixedButton";
import { AxiosResponse } from "axios";
import { QuotationResponse } from "@/apis/sangjoQuotation";
import { useRouter } from "next/router";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { isLoggedInState } from "@/atoms/accountState";
import useSangjoQuotationRequests from "@/hooks/useSangjoQuotationRequests";
import { useSangjoQuotationRequestExistModal } from "@/hooks/useSangjoQuotationRequestExistModal";
import { matchRequestResDataState } from "@/atoms/match/matchRequestResDataState";
import { useOverlay } from "@toss/use-overlay";
import CustomRequestModalContent from "@/components/Match/CustomRequestModalContent";
import { Button } from "@chakra-ui/react";
import { useCustomVariation } from "@/hooks/useCustomVariation";
import { Axios } from "@/utils/axios";
import { quotationRequestState } from "@/atoms/quotationRequestState";

const 조문객수ServiceType = {
  "0명(무빈소)": "무빈소",
  "50명 내외": "가족장",
  "100명 내외": "가족장",
  "200명 이상": "일반장",
  모르겠어요: "가족장",
};

interface Props {
  selectedOption: SelectedOption;
  isUniqueButton?: boolean;
  isTesting?: boolean;
  disabled?: boolean;
}

const AllQuotationRequestButtonGroup = ({
  isTesting,
  selectedOption,
  isUniqueButton,
  disabled,
}: Props) => {
  const router = useRouter();
  const isLoggedIn = useRecoilValue(isLoggedInState);
  const setResponseData = useSetRecoilState<any>(matchRequestResDataState);
  const setQuotationRequest = useSetRecoilState(quotationRequestState);

  const { setQuotationRequestCookie, mutateSangjoQuotation, isLoading } =
    useSangjoQuotationRequests();
  const { onOpenRequestExistModal } = useSangjoQuotationRequestExistModal();
  const overlay = useOverlay();
  const variation = useCustomVariation(121);
  const isDisabled =
    !selectedOption.장례준비긴급도 ||
    !selectedOption.sido ||
    !selectedOption.sigungu ||
    !selectedOption.조문객수;

  const sendConversionEvent = () => {
    customEvent({
      newGtm: {
        path: "match_request",
        trigger_and_target: "submit_conversion",
      },
      hackle: { key: HackleEventName["견적 요청 제출 클릭"] },
    });
  };

  const parseCondolers = (조문객수: string) => {
    if (조문객수 === "없음") {
      return "";
    }

    if (조문객수 === "0명(무빈소)") {
      return "0명";
    }

    return 조문객수;
  };

  const parseRequestBody = (selectedOption: SelectedOption): QuotationRequestBody => {
    return {
      status: selectedOption.장례준비긴급도,
      funeral_sido: selectedOption.sido as number,
      funeral_sigungu: selectedOption.sigungu as number,
      needs_binso: selectedOption.조문객수 === "0명(무빈소)" ? false : true,
      condolers: parseCondolers(selectedOption.조문객수),
      is_sequential: false,
    };
  };

  const handleProceed = async () => {
    if (isLoading) {
      return;
    }

    sendConversionEvent();

    const body = parseRequestBody(selectedOption);

    const onSuccess = ({ data }: AxiosResponse<QuotationResponse>) => {
      customEvent({
        newGtm: {
          action: "match_request_complete",
        },
      });

      if (isLoggedIn) {
        // router.push(
        //   `/match/request-complete?id=${data.id}&sido=${data.funeral_sido}&sigungu=${data.funeral_sigungu}`,
        // );
      } else {
        setResponseData(data);
        setQuotationRequestCookie(data);

        router.push("/match-compare/detail-request/");
      }
    };

    mutateSangjoQuotation({
      body,
      onSuccess,
      onError: (error) => {
        if (error?.response?.status === 409) {
          onOpenRequestExistModal();
        }
      },
    });
  };

  const handleSangjoQuotationRequest = async () => {
    customEvent({
      newGtm: {
        path: "match_request",
        trigger_and_target: "mdl_check_result",
      },
      triggerHackle: true,
    });

    const anyMemo = getCookie("match_request_memo");
    const previousRequestedQuotation = getCookie(cooKeys.quotation_request);

    const { data } = await Axios.patch(
      `/sangjo_quotation_requests/${previousRequestedQuotation.id}/`,
      {
        memo: anyMemo || "",
      },
    );

    if (!isLoggedIn) {
      setCookie(
        cooKeys.quotation_request,
        JSON.stringify({ ...data, id: previousRequestedQuotation.id }),
      );

      setQuotationRequest(data);

      router.push(
        `/login?redirectTo=${encodeURIComponent(`/match/request/`)}${`&event_from=uv_mtch_rqst`}`,
      );
    } else {
      router.push(
        `/match/request-complete?id=${data.id}&sido=${data.funeral_sido}&sigungu=${data.funeral_sigungu}`,
      );
    }
  };
  const handleSubmit = async () => {
    if (isLoading) {
      return;
    }

    sendConversionEvent();

    const body = parseRequestBody(selectedOption);

    const onSuccess = ({ data }: AxiosResponse<QuotationResponse>) => {
      customEvent({
        newGtm: {
          action: "match_request_complete",
        },
      });

      if (isLoggedIn) {
        // router.push(
        //   `/match/request-complete?id=${data.id}&sido=${data.funeral_sido}&sigungu=${data.funeral_sigungu}`,
        // );
      } else {
        setResponseData(data);
        setQuotationRequestCookie(data);

        if (variation === "A") {
          overlay.open(({ isOpen, close: onClose }) => {
            return (
              <OverlayModal
                onClose={onClose}
                isOpen={isOpen}
                modalContentStyle={{
                  minWidth: "320px",
                  overflow: "scroll",
                }}
                overlayModalContent={
                  <CustomRequestModalContent
                    eventFrom="uv_mtch_rqst"
                    serviceType={
                      조문객수ServiceType[
                        selectedOption.조문객수 as keyof typeof 조문객수ServiceType
                      ]
                    }
                    onClose={onClose}
                  />
                }
              />
            );
          });
        } else {
          handleSangjoQuotationRequest();
        }
      }
    };

    mutateSangjoQuotation({
      body,
      onSuccess,
      onError: (error) => {
        if (error?.response?.status === 409) {
          onOpenRequestExistModal();
        }
      },
    });
  };

  return isTesting ? (
    <Button
      onClick={handleSubmit}
      isLoading={isLoading}
      isDisabled={disabled}
      background={colors.orange650}
      color="white"
      _hover={{ background: colors.orange500 }}
      _disabled={{
        background: colors.orange400,
      }}
      borderRadius="36px"
      width="100%"
    >
      다음
    </Button>
  ) : isUniqueButton ? (
    <RequestUniqueFixedButton
      disabled={isDisabled}
      handleSubmit={handleSubmit}
      loading={isLoading}
    />
  ) : (
    <RequestCompareFixedButton
      disabled={isDisabled}
      handleSubmit={handleSubmit}
      handleProceed={handleProceed}
      loading={isLoading}
    />
  );
};

export default AllQuotationRequestButtonGroup;
